import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { A, card, btnPrimary, btnSecondary, STATUS_COLORS } from '../../adminTheme';

const AdminUsers = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [saving, setSaving] = useState(false);
  const [pwModal, setPwModal] = useState(null); // { id, name }
  const [newPw, setNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const { admin: currentAdmin } = useAuth();

  const fetchAdmins = async () => {
    setLoading(true);
    try { const res = await adminApi.getAdmins(); setAdmins(res.data.data || []); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.register(form);
      toast.success('User added!');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'staff' });
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await adminApi.deleteAdmin(id); toast.success('Deleted'); fetchAdmins(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPw || newPw.length < 6) { toast.error('Password minimum 6 characters'); return; }
    setPwSaving(true);
    try {
      await adminApi.changeStaffPassword(pwModal.id, { new_password: newPw });
      toast.success(`${pwModal.name} ka password update ho gaya!`);
      setPwModal(null);
      setNewPw('');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setPwSaving(false); }
  };

  const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 14, border: `1.5px solid ${A.border1}`, background: A.surface3, color: A.text1, fontFamily: 'inherit', outline: 'none' };

  return (
    <div>
      <h1 style={{fontFamily:'Playfair Display,serif', fontSize:24, fontWeight:700, color:'#1a0f00', margin:'0 0 20px'}}>Users & Roles</h1>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <p style={{ color: A.text3, fontSize: 14 }}>{admins.length} user{admins.length !== 1 ? 's' : ''}</p>
        {currentAdmin?.role === 'admin' && (
          <button onClick={() => setShowAdd(true)} style={{ ...btnPrimary, padding: '9px 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            + Add User
          </button>
        )}
      </div>

      <div style={{ ...card({ padding: 0, overflow: 'hidden' }) }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            {Array(3).fill(0).map((_, i) => (
              <div key={i} style={{ height: 60, borderRadius: 8, background: A.surface3, marginBottom: 8, animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 24px', background: A.surface2, borderBottom: `1px solid ${A.border1}` }}>
              {['User', 'Email', 'Role', 'Joined', 'Action'].map(h => (
                <div key={h} style={{ flex: h === 'User' ? 2 : 1, color: A.text3, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
              ))}
            </div>

            {admins.map((user, i) => {
              const roleSc = STATUS_COLORS[user.role] || STATUS_COLORS.staff;
              const isYou = user.id === currentAdmin?.id;
              return (
                <div key={user.id} style={{
                  display: 'flex', alignItems: 'center', padding: '14px 24px',
                  borderBottom: i < admins.length - 1 ? `1px solid ${A.border2}` : 'none',
                  transition: 'background 0.1s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = A.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  {/* User */}
                  <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: `linear-gradient(135deg, ${A.accent}, ${A.accentHover})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700, color: 'white',
                    }}>{user.name?.[0]}</div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: A.text1, fontWeight: 600, fontSize: 14 }}>{user.name}</span>
                        {isYou && <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 99, background: A.accentSoft, color: A.accent, fontWeight: 600 }}>You</span>}
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ flex: 1, color: A.text3, fontSize: 13 }}>{user.email}</div>

                  {/* Role */}
                  <div style={{ flex: 1 }}>
                    <span style={{ display:'inline-flex', padding:'3px 12px', borderRadius:99, fontSize:11, fontWeight:600, textTransform:'capitalize', background:roleSc.bg, color:roleSc.color, border:`1px solid ${roleSc.border}` }}>
                      {user.role}
                    </span>
                  </div>

                  {/* Date */}
                  <div style={{ flex: 1, color: A.text4, fontSize: 12 }}>{new Date(user.created_at).toLocaleDateString('en-IN')}</div>

                  {/* Action */}
                  <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                    {currentAdmin?.role === 'admin' && !isYou && (
                      <>
                        <button onClick={() => { setPwModal({ id: user.id, name: user.name }); setNewPw(''); }}
                          style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${A.accentBorder || '#f0d0b0'}`, background: A.accentSoft, color: A.accent, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
                          🔑 Password
                        </button>
                        <button onClick={() => handleDelete(user.id)}
                          style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${A.redBorder}`, background: A.redBg, color: A.red, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add modal */}
      <AnimatePresence>
        {showAdd && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={() => setShowAdd(false)} />
            <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              style={{ ...card({ padding: 28 }), position: 'relative', width: '100%', maxWidth: 400 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <p style={{ fontFamily: '"Playfair Display",serif', fontSize: 18, fontWeight: 700, color: A.text1, margin: 0 }}>Add New User</p>
                <button onClick={() => setShowAdd(false)} style={{ background: A.surface3, border: `1px solid ${A.border1}`, borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: A.text3 }}>✕</button>
              </div>
              <form onSubmit={handleAdd} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { key: 'name',     label: 'Full Name',  type: 'text',     ph: 'John Smith'    },
                  { key: 'email',    label: 'Email',      type: 'email',    ph: 'john@cafe.com' },
                  { key: 'password', label: 'Password',   type: 'password', ph: 'Min 6 chars'   },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', color: A.text3, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label} *</label>
                    <input type={f.type} required placeholder={f.ph} minLength={f.key === 'password' ? 6 : undefined}
                      value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={inp}
                      onFocus={e => { e.target.style.borderColor = A.accent; e.target.style.boxShadow = `0 0 0 3px ${A.accentSoft}`; }}
                      onBlur={e => { e.target.style.borderColor = A.border1; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', color: A.text3, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Role</label>
                  <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={inp}>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                  <button type="button" onClick={() => setShowAdd(false)} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ ...btnPrimary, flex: 1, opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Adding...' : 'Add User'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Change Password Modal */}
      <AnimatePresence>
        {pwModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={() => setPwModal(null)} />
            <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              style={{ ...card({ padding: 28 }), position: 'relative', width: '100%', maxWidth: 380 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <p style={{ fontFamily: '"Playfair Display",serif', fontSize: 18, fontWeight: 700, color: A.text1, margin: 0 }}>Change Password</p>
                  <p style={{ color: A.text3, fontSize: 13, margin: '4px 0 0' }}>🔑 {pwModal.name}</p>
                </div>
                <button onClick={() => setPwModal(null)} style={{ background: A.surface3, border: `1px solid ${A.border1}`, borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: A.text3 }}>✕</button>
              </div>
              <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', color: A.text3, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>New Password *</label>
                  <input type="password" required placeholder="Min 6 characters" minLength={6}
                    value={newPw} onChange={e => setNewPw(e.target.value)}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 14, border: `1.5px solid ${A.border1}`, background: A.surface3, color: A.text1, fontFamily: 'inherit', outline: 'none' }}
                    onFocus={e => { e.target.style.borderColor = A.accent; e.target.style.boxShadow = `0 0 0 3px ${A.accentSoft}`; }}
                    onBlur={e => { e.target.style.borderColor = A.border1; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button type="button" onClick={() => setPwModal(null)} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
                  <button type="submit" disabled={pwSaving} style={{ ...btnPrimary, flex: 1, opacity: pwSaving ? 0.7 : 1 }}>
                    {pwSaving ? 'Saving...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}`}</style>
    </div>
  );
};

export default AdminUsers;
